import React from "react";
import { AcxMainTextFieldRef } from "components/UI/AcxMainTextField";
import { getCaretCoordinates } from "components/Classifiers/LLMClassifiers/Components/LogicChainBuilder/utils";
import { UserWithFullName } from "components/Signals/Dashboard/DashboardMenu/ShareDialog/constants";
import { MENTION_DROPDOWN_WIDTH } from "./MentionDropdown";

type UseMentionsArgs = {
    users: UserWithFullName[] | null | undefined;
    usersLoading: boolean;
    inputRef: React.RefObject<AcxMainTextFieldRef>;
    inputValue: string;
};

const useMentions = ({
    users,
    usersLoading,
    inputRef,
    inputValue,
}: UseMentionsArgs) => {
    const [mentionQuery, setMentionQuery] = React.useState("");
    const [mentionStartIndex, setMentionStartIndex] = React.useState<
        number | null
    >(null);
    const [mentionSelections, setMentionSelections] = React.useState<
        UserWithFullName[]
    >([]);
    // Track caret position so mention detection works when inserting "@" mid-text.
    const [cursorPosition, setCursorPosition] = React.useState<number | null>(
        null,
    );
    const [mentionDropdownPosition, setMentionDropdownPosition] =
        React.useState<{
            top: number;
            left: number;
        } | null>(null);
    const [activeMentionIndex, setActiveMentionIndex] = React.useState(0);
    const mentionDropdownRef = React.useRef<HTMLDivElement | null>(null);
    const mentionAnchorPositionRef = React.useRef<number | null>(null);
    const mentionAnchorIndexRef = React.useRef<number | null>(null);

    const setMentionState = React.useCallback(
        (query: string, startIndex: number | null) => {
            setMentionQuery(query);
            setMentionStartIndex(startIndex);
        },
        [],
    );

    const matchingUsers = React.useMemo(() => {
        if (!mentionQuery || usersLoading || !users) return [];
        const normalizedQuery = mentionQuery.toLowerCase();
        return users
            .filter((user) => {
                const fullName = user.fullName?.toLowerCase() ?? "";
                const email = user.email?.toLowerCase() ?? "";
                return (
                    fullName.includes(normalizedQuery) ||
                    email.includes(normalizedQuery)
                );
            })
            .slice(0, 6);
    }, [mentionQuery, users, usersLoading]);

    const shouldShowMentions =
        mentionStartIndex !== null && matchingUsers.length > 0;

    const syncCursorFromEvent = React.useCallback(
        (
            event: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => {
            const target = event.currentTarget;
            if (typeof target.selectionStart === "number") {
                setCursorPosition(target.selectionStart);
            }
        },
        [],
    );

    const updateMentionDropdownPosition = React.useCallback(
        (position?: number) => {
            const input = inputRef.current;
            if (!input) return;
            const caretPosition =
                position ?? input.selectionStart ?? inputValue.length;
            const caret = getCaretCoordinates(input, caretPosition);
            const inputRect = input.getBoundingClientRect();
            const viewportPadding = 16;
            const maxLeft = Math.max(
                viewportPadding,
                window.innerWidth - MENTION_DROPDOWN_WIDTH - viewportPadding,
            );
            setMentionDropdownPosition({
                top: inputRect.top + caret.top + caret.height + 8,
                left: Math.min(inputRect.left + caret.left, maxLeft),
            });
        },
        [inputRef, inputValue.length],
    );

    React.useEffect(() => {
        const position = cursorPosition ?? inputValue.length;
        const textBeforeCursor = inputValue.slice(0, position);
        const lastAt = textBeforeCursor.lastIndexOf("@");
        // Only open autocomplete for the "@" closest to the caret.
        if (lastAt === -1) {
            setMentionState("", null);
            return;
        }
        const prevChar = lastAt > 0 ? textBeforeCursor[lastAt - 1] : "";
        if (prevChar && !/\s/.test(prevChar)) {
            setMentionState("", null);
            return;
        }
        const afterAt = textBeforeCursor.slice(lastAt + 1);
        if (afterAt.length === 0) {
            setMentionState("", lastAt);
            return;
        }
        if (/\s/.test(afterAt)) {
            setMentionState("", null);
            return;
        }
        setMentionState(afterAt, lastAt);
    }, [inputValue, cursorPosition, setMentionState]);

    React.useEffect(() => {
        setActiveMentionIndex(0);
    }, [mentionStartIndex, mentionQuery, matchingUsers.length]);

    React.useEffect(() => {
        if (!shouldShowMentions) return;

        const handleDocumentMouseDown = (event: MouseEvent) => {
            const target = event.target as Node | null;
            const input = inputRef.current;
            const dropdown = mentionDropdownRef.current;
            if (
                (input && "contains" in input && input.contains(target)) ||
                dropdown?.contains(target)
            ) {
                return;
            }
            setMentionQuery("");
            setMentionStartIndex(null);
        };

        document.addEventListener("mousedown", handleDocumentMouseDown);
        return () => {
            document.removeEventListener("mousedown", handleDocumentMouseDown);
        };
    }, [inputRef, shouldShowMentions]);

    React.useEffect(() => {
        if (mentionStartIndex === null) {
            mentionAnchorPositionRef.current = null;
            mentionAnchorIndexRef.current = null;
            return;
        }
        if (mentionAnchorIndexRef.current !== mentionStartIndex) {
            const input = inputRef.current;
            mentionAnchorIndexRef.current = mentionStartIndex;
            mentionAnchorPositionRef.current =
                input?.selectionStart ?? inputValue.length;
        }
    }, [mentionStartIndex, inputRef, inputValue.length]);

    React.useEffect(() => {
        if (!shouldShowMentions) {
            setMentionDropdownPosition(null);
            return;
        }
        updateMentionDropdownPosition(
            mentionAnchorPositionRef.current ?? inputValue.length,
        );
    }, [shouldShowMentions, updateMentionDropdownPosition, inputValue.length]);

    const handleMentionKeyDown = React.useCallback(
        (
            event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
            onSelect: (user: UserWithFullName) => void,
        ) => {
            if (!shouldShowMentions) return;
            switch (event.key) {
                case "ArrowDown":
                    event.preventDefault();
                    setActiveMentionIndex(
                        (prev) => (prev + 1) % matchingUsers.length,
                    );
                    break;
                case "ArrowUp":
                    event.preventDefault();
                    setActiveMentionIndex(
                        (prev) =>
                            (prev - 1 + matchingUsers.length) %
                            matchingUsers.length,
                    );
                    break;
                case "Enter":
                    event.preventDefault();
                    onSelect(matchingUsers[activeMentionIndex]);
                    break;
                case "Escape":
                    event.preventDefault();
                    setMentionQuery("");
                    setMentionStartIndex(null);
                    break;
                default:
                    break;
            }
        },
        [
            activeMentionIndex,
            matchingUsers,
            setActiveMentionIndex,
            setMentionQuery,
            setMentionStartIndex,
            shouldShowMentions,
        ],
    );

    return {
        state: {
            matchingUsers,
            mentionQuery,
            mentionSelections,
            mentionStartIndex,
            mentionDropdownPosition,
            activeMentionIndex,
            shouldShowMentions,
        },
        actions: {
            setMentionQuery,
            setMentionStartIndex,
            setMentionSelections,
            setActiveMentionIndex,
            setCursorPosition,
            syncCursorFromEvent,
            handleMentionKeyDown,
        },
        refs: {
            mentionDropdownRef,
        },
    };
};

export default useMentions;
